import { Controller } from "stimulus"

// autcomplete for companies search in modals
export default class extends Controller {
    static targets = [ "container", "input", "sortBtn", "companiesList", "locationFilter", "sectorFilter", "totalCount", "downloadBtn" ]
    
    connect() {
       console.log("Companies list!")
       this.currentSortField = null
       this.currentSortDirection = 'asc'
       this.currentPage = 0
       this.currentCountries = []
       this.currentRegions = []
       this.currentSectors = []
       this.searchQuery = ''
    }

    // Parse location filter selections into countries and regions
    parseLocationFilter() {
      if (!this.hasLocationFilterTarget) {
        return { countries: [], regions: [] }
      }

      const selectedValues = Array.from(this.locationFilterTarget.selectedOptions).map(opt => opt.value)
      const countries = []
      const regions = []

      selectedValues.forEach(value => {
        if (value.startsWith('region:')) {
          regions.push(value.replace('region:', ''))
        } else if (value.startsWith('country:')) {
          countries.push(value.replace('country:', ''))
        }
      })

      return { countries, regions }
    }

    findCompanies(event) {
      this.searchQuery = event.target.value
      const { countries, regions } = this.parseLocationFilter()
      
      // Show/hide download button based on filters
      this.updateDownloadButtonVisibility()
      
      // Build query with filters
      let url = `/companies/filtered?query=${encodeURIComponent(this.searchQuery)}`
      if (countries.length > 0) {
        countries.forEach(country => {
          url += `&countries[]=${encodeURIComponent(country)}`
        })
      }
      if (regions.length > 0) {
        regions.forEach(region => {
          url += `&regions[]=${encodeURIComponent(region)}`
        })
      }
      if (this.currentSectors.length > 0) {
        this.currentSectors.forEach(sector => {
          url += `&sectors[]=${encodeURIComponent(sector)}`
        })
      }
      
      fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      .then(response => response.json())
      .then((data) => {
        // Remove all dynamically added items from infinite scroll
        if (this.hasCompaniesListTarget && this.hasContainerTarget) {
          let sibling = this.companiesListTarget.nextElementSibling
          while (sibling) {
            const nextSibling = sibling.nextElementSibling
            if (!sibling.hasAttribute('data-infinite-scroll-target') && 
                !sibling.hasAttribute('data-companies-list-target')) {
              sibling.remove()
            }
            sibling = nextSibling
          }
        }
        
        if (this.hasCompaniesListTarget) {
          this.companiesListTarget.innerHTML = data.html
        }
        if (this.hasTotalCountTarget) {
          this.totalCountTarget.textContent = `${data.count} stocks`
        }
      })
      .catch(error => {
        console.error('Error searching companies:', error)
      })
    }

    applyFilters() {
      console.log("=== applyFilters called ===")
      console.log("hasSectorFilterTarget:", this.hasSectorFilterTarget)
      
      // Get selected values from multi-select dropdowns
      const { countries, regions } = this.parseLocationFilter()
      this.currentCountries = countries
      this.currentRegions = regions
      this.currentSectors = this.hasSectorFilterTarget ? 
        Array.from(this.sectorFilterTarget.selectedOptions).map(opt => opt.value) : []
      
      console.log("Selected sectors:", this.currentSectors)
      console.log("Selected countries:", this.currentCountries)
      console.log("Selected regions:", this.currentRegions)
      
      // Show/hide download button based on filters
      this.updateDownloadButtonVisibility()
      
      // Reset sorting when filters change
      this.currentSortField = null
      this.currentSortDirection = 'asc'
      this.currentPage = 0
      this.resetAllSortIcons()
      
      // Build query with filters
      let url = `/companies/filtered?query=${encodeURIComponent(this.searchQuery)}`
      if (this.currentCountries.length > 0) {
        this.currentCountries.forEach(country => {
          url += `&countries[]=${encodeURIComponent(country)}`
        })
      }
      if (this.currentRegions.length > 0) {
        this.currentRegions.forEach(region => {
          url += `&regions[]=${encodeURIComponent(region)}`
        })
      }
      if (this.currentSectors.length > 0) {
        this.currentSectors.forEach(sector => {
          url += `&sectors[]=${encodeURIComponent(sector)}`
        })
      }
      
      fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      .then(response => response.json())
      .then((data) => {
        // Remove all dynamically added items from infinite scroll
        if (this.hasCompaniesListTarget && this.hasContainerTarget) {
          let sibling = this.companiesListTarget.nextElementSibling
          while (sibling) {
            const nextSibling = sibling.nextElementSibling
            if (!sibling.hasAttribute('data-infinite-scroll-target') && 
                !sibling.hasAttribute('data-companies-list-target')) {
              sibling.remove()
            }
            sibling = nextSibling
          }
        }
        
        if (this.hasCompaniesListTarget) {
          this.companiesListTarget.innerHTML = data.html
        }
        if (this.hasTotalCountTarget) {
          this.totalCountTarget.textContent = `${data.count} stocks`
        }
        
        // Reset infinite scroll counter
        const infiniteScrollElement = document.querySelector('[data-controller*="infinite-scroll"]')
        if (infiniteScrollElement) {
          const infiniteScrollController = this.application.getControllerForElementAndIdentifier(
            infiniteScrollElement,
            'infinite-scroll'
          )
          if (infiniteScrollController) {
            infiniteScrollController.hitCounter = 0
          }
        }
      })
      .catch(error => {
        console.error('Error applying filters:', error)
      })
    }

    clearFilters() {
      // Reset filter values
      if (this.hasLocationFilterTarget) {
        Array.from(this.locationFilterTarget.options).forEach(opt => opt.selected = false)
      }
      if (this.hasSectorFilterTarget) {
        Array.from(this.sectorFilterTarget.options).forEach(opt => opt.selected = false)
      }
      if (this.hasInputTarget) this.inputTarget.value = ''
      
      this.currentCountries = []
      this.currentRegions = []
      this.currentSectors = []
      this.searchQuery = ''
      this.currentSortField = null
      this.currentSortDirection = 'asc'
      this.currentPage = 0
      
      // Hide download button when filters are cleared
      this.updateDownloadButtonVisibility()
      
      this.resetAllSortIcons()
      
      // Remove all dynamically added items from infinite scroll
      // The infinite scroll appends items directly to the container after companiesList
      if (this.hasCompaniesListTarget && this.hasContainerTarget) {
        // Get all siblings after companiesList and remove them (these are infinite scroll additions)
        let sibling = this.companiesListTarget.nextElementSibling
        while (sibling) {
          const nextSibling = sibling.nextElementSibling
          // Don't remove the loading bar or sort header
          if (!sibling.hasAttribute('data-infinite-scroll-target') && 
              !sibling.hasAttribute('data-companies-list-target')) {
            sibling.remove()
          }
          sibling = nextSibling
        }
      }
      
      // Load all companies without any filters
      let url = '/companies/filtered?query='
      
      fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      .then(response => response.json())
      .then((data) => {
        if (this.hasCompaniesListTarget) {
          this.companiesListTarget.innerHTML = data.html
        }
        if (this.hasTotalCountTarget) {
          this.totalCountTarget.textContent = `${data.count} stocks`
        }
        
        // Reset infinite scroll counter
        const infiniteScrollElement = document.querySelector('[data-controller*="infinite-scroll"]')
        if (infiniteScrollElement) {
          const infiniteScrollController = this.application.getControllerForElementAndIdentifier(
            infiniteScrollElement,
            'infinite-scroll'
          )
          if (infiniteScrollController) {
            infiniteScrollController.hitCounter = 0
          }
        }
      })
      .catch(error => {
        console.error('Error clearing filters:', error)
      })
    }

    sortBy(event) {
      const sortField = event.currentTarget.dataset.sortField
      const sortBtn = event.currentTarget
      
      // Reset all other buttons first (exclusive sorting)
      this.resetAllSortIcons()
      
      // Toggle sort direction if clicking the same field
      if (this.currentSortField === sortField) {
        this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.currentSortDirection = 'asc'
        this.currentSortField = sortField
      }
      
      // Update button icons
      this.updateSortIcons(sortBtn, this.currentSortDirection)
      
      // Reset pagination when sorting changes
      this.currentPage = 0
      
      // Reset infinite scroll counter
      const infiniteScrollElement = document.querySelector('[data-controller*="infinite-scroll"]')
      if (infiniteScrollElement) {
        const infiniteScrollController = this.application.getControllerForElementAndIdentifier(
          infiniteScrollElement,
          'infinite-scroll'
        )
        if (infiniteScrollController) {
          infiniteScrollController.hitCounter = 0
        }
      }
      
      // Make AJAX request to get sorted companies
      this.fetchSortedCompanies(sortField, this.currentSortDirection)
    }
    
    resetAllSortIcons() {
      // Reset all sort icons to neutral state
      this.sortBtnTargets.forEach(btn => {
        const icon = btn.querySelector('i')
        icon.className = 'fa-solid fa-sort text-muted'
        icon.style.fontSize = '0.8rem'
      })
    }

    updateSortIcons(activeBtn, direction) {
      // Update only the active button icon
      const activeIcon = activeBtn.querySelector('i')
      if (direction === 'asc') {
        activeIcon.className = 'fa-solid fa-sort-up text-primary'
      } else {
        activeIcon.className = 'fa-solid fa-sort-down text-primary'
      }
      activeIcon.style.fontSize = '0.8rem'
    }
    
    fetchSortedCompanies(sortField, direction) {
      let url = `/companies/sorted?sort_field=${sortField}&sort_direction=${direction}`
      
      // Add filters to sorting request
      if (this.currentCountries.length > 0) {
        this.currentCountries.forEach(country => {
          url += `&countries[]=${encodeURIComponent(country)}`
        })
      }
      if (this.currentRegions.length > 0) {
        this.currentRegions.forEach(region => {
          url += `&regions[]=${encodeURIComponent(region)}`
        })
      }
      if (this.currentSectors.length > 0) {
        this.currentSectors.forEach(sector => {
          url += `&sectors[]=${encodeURIComponent(sector)}`
        })
      }
      if (this.searchQuery) url += `&query=${encodeURIComponent(this.searchQuery)}`
      
      fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      .then(response => {
        console.log('Response status:', response.status)
        console.log('Response headers:', response.headers)
        
        if (!response.ok) {
          return response.text().then(text => {
            console.error('Response body:', text)
            throw new Error(`HTTP error! status: ${response.status}, body: ${text}`)
          })
        }
        
        const contentType = response.headers.get('content-type')
        console.log('Content type:', contentType)
        
        if (!contentType || !contentType.includes('application/json')) {
          return response.text().then(text => {
            console.error('Non-JSON response body:', text)
            throw new Error('Response is not JSON: ' + text.substring(0, 200))
          })
        }
        return response.json()
      })
      .then(data => {
        console.log('Sorted data received:', data)
        // Remove all dynamically added items from infinite scroll
        if (this.hasCompaniesListTarget && this.hasContainerTarget) {
          let sibling = this.companiesListTarget.nextElementSibling
          while (sibling) {
            const nextSibling = sibling.nextElementSibling
            if (!sibling.hasAttribute('data-infinite-scroll-target') && 
                !sibling.hasAttribute('data-companies-list-target')) {
              sibling.remove()
            }
            sibling = nextSibling
          }
        }
        
        // Clear current list and insert sorted companies
        if (this.hasCompaniesListTarget) {
          this.companiesListTarget.innerHTML = data.html
        } else {
          console.warn('companiesListTarget not found')
        }
        if (this.hasTotalCountTarget && data.count !== undefined) {
          this.totalCountTarget.textContent = `${data.count} stocks`
        }
      })
      .catch(error => {
        console.error('Error fetching sorted companies:', error)
        console.error('URL:', url)
        console.error('Full error details:', error.message)
      })
    }

    getCSRFToken() {
      const metaTag = document.querySelector('meta[name="csrf-token"]')
      return metaTag ? metaTag.getAttribute('content') : ''
    }

    // Method to get current sort state (called by infinite scroll)
    getCurrentSortState() {
      return {
        sortField: this.currentSortField,
        sortDirection: this.currentSortDirection,
        hasSorting: this.currentSortField !== null
      }
    }

    // Method to load more companies with current sort/filters (called by infinite scroll)
    loadMoreSortedCompanies(page) {
      const hasFilters = this.currentCountries.length > 0 || this.currentRegions.length > 0 || this.currentSectors.length > 0 || this.searchQuery.length > 0
      
      // If no sorting and no filters, let infinite scroll handle normally
      if (!this.currentSortField && !hasFilters) {
        return Promise.resolve(null)
      }

      let url = ''
      
      if (this.currentSortField) {
        // Use sorted endpoint
        url = `/companies/sorted?sort_field=${this.currentSortField}&sort_direction=${this.currentSortDirection}&page=${page}`
      } else {
        // Use filtered endpoint with pagination
        url = `/companies/filtered?page=${page}`
      }
      
      // Add filters to pagination request
      if (this.currentCountries.length > 0) {
        this.currentCountries.forEach(country => {
          url += `&countries[]=${encodeURIComponent(country)}`
        })
      }
      if (this.currentRegions.length > 0) {
        this.currentRegions.forEach(region => {
          url += `&regions[]=${encodeURIComponent(region)}`
        })
      }
      if (this.currentSectors.length > 0) {
        this.currentSectors.forEach(sector => {
          url += `&sectors[]=${encodeURIComponent(sector)}`
        })
      }
      if (this.searchQuery) url += `&query=${encodeURIComponent(this.searchQuery)}`
      
      return fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        return data.html
      })
    }

    updateDownloadButtonVisibility() {
      // if (!this.hasDownloadBtnTarget) return
      
      // const hasFilters = this.currentCountries.length > 0 || 
      //                   this.currentRegions.length > 0 || 
      //                   this.currentSectors.length > 0 || 
      //                   this.searchQuery.length > 0
      
      // if (hasFilters) {
      //   this.downloadBtnTarget.classList.remove('d-none')
      // } else {
      //   this.downloadBtnTarget.classList.add('d-none')
      // }
    }

    showProviders(event) {
      this.setProvidersCollapse(event.currentTarget, true)
    }

    hideProviders(event) {
      this.setProvidersCollapse(event.currentTarget, false)
    }

    setProvidersCollapse(container, shouldShow) {
      const collapseId = container.dataset.providerCollapseId
      if (!collapseId) return

      const collapseElement = document.getElementById(collapseId)
      if (!collapseElement) return

      if (window.bootstrap && window.bootstrap.Collapse) {
        const collapse = window.bootstrap.Collapse.getOrCreateInstance(collapseElement, { toggle: false })
        shouldShow ? collapse.show() : collapse.hide()
      } else {
        collapseElement.classList.toggle('show', shouldShow)
      }

      const trigger = container.querySelector(`[data-bs-target="#${collapseId}"]`)
      if (trigger) trigger.setAttribute('aria-expanded', shouldShow.toString())
    }

    downloadExcel(event) {
      event.preventDefault()
      
      // Build URL with current filters
      let url = '/companies/download_filtered_companies?'
      
      // Add query parameter
      if (this.searchQuery) {
        url += `query=${encodeURIComponent(this.searchQuery)}`
      }
      
      // Add country filters
      if (this.currentCountries.length > 0) {
        this.currentCountries.forEach(country => {
          url += `&countries[]=${encodeURIComponent(country)}`
        })
      }
      
      // Add region filters
      if (this.currentRegions.length > 0) {
        this.currentRegions.forEach(region => {
          url += `&regions[]=${encodeURIComponent(region)}`
        })
      }
      
      // Add sector filters
      if (this.currentSectors.length > 0) {
        this.currentSectors.forEach(sector => {
          url += `&sectors[]=${encodeURIComponent(sector)}`
        })
      }
      
      // Disable button and show loading state
      const btn = event.currentTarget
      const originalText = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin me-1"></i>Generating...'
      
      // Trigger download by navigating to URL
      window.location.href = url
      
      // Re-enable button after a delay
      setTimeout(() => {
        btn.disabled = false
        btn.innerHTML = originalText
      }, 2000)
    }
}
